import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiBase, detectTarget, type Target } from './pos/api';
import { Login } from './pos/Login';
import { Till, type Session } from './pos/Till';
import { SetupScreen } from './pos/SetupScreen';
import { needsSetup, shouldKeepAsking } from './pos/setup';
import { OpenShiftScreen } from './pos/OpenShiftScreen';
import { ExitCorner } from './pos/exit';

/**
 * The till, from cold boot.
 *
 * Four gates now, in order: know what we are talking to, then signed in, then a
 * shift open, then selling. A cashier who reaches the sale screen without a
 * shift produces invoices that belong to no session — and those are exactly the
 * ones that cannot be reconciled at the end of the day, because nothing says
 * which drawer their cash went into.
 *
 * The first gate exists because the same bundle runs against the cloud and
 * against the till's own agent, and only one of them has a login. See
 * detectTarget() in pos/api.ts.
 */
export function App() {
  const qc = useQueryClient();
  const [target, setTarget] = useState<Target | null>(null);
  const [authed, setAuthed] = useState(() => Boolean(localStorage.getItem('luma.till.token')));

  const [probe, setProbe] = useState(0);
  useEffect(() => { void detectTarget().then(setTarget); }, [probe]);

  // A session that dies mid-shift takes the app back to login rather than
  // leaving every button silently doing nothing.
  useEffect(() => {
    const expire = () => setAuthed(false);
    window.addEventListener('luma.session-expired', expire);
    return () => window.removeEventListener('luma.session-expired', expire);
  }, []);

  // On the agent there is nobody to sign in: the device is the credential, and
  // the session comes back on the first call.
  const ready = target === 'agent' || (target === 'cloud' && authed);

  const session = useQuery({
    queryKey: ['till-session'],
    queryFn: () => api.get<Session & { branches: { id: string; name: string }[] }>('/till/session'),
    enabled: ready,
  });

  /*
   * Has this till been paired at all?
   *
   * Asked of the agent rather than inferred from an empty branch on the
   * session, because the session answers with placeholders when there is no
   * identity -- a branch called «شعبه» and a shop called nothing -- and a
   * screen that reads those as real shows a fresh install as a working till.
   */
  const status = useQuery({
    queryKey: ['till-status'],
    queryFn: () => api.get<{ activated?: boolean }>('/status'),
    enabled: target === 'agent',
    retry: false,
    /*
     * Keep asking while this till has no identity.
     *
     * Pairing restarts the agent, so for several seconds afterwards nothing
     * answers on the loopback API. The first version asked ONCE, on a six
     * second timer guessed to be long enough; the service took longer, the one
     * refetch failed, and the screen sat on "در حال آماده‌سازی" forever with
     * nothing left to retry. The shopkeeper's till had in fact paired.
     *
     * So the screen is driven by the agent coming back rather than by a delay
     * somebody guessed. Polling stops the moment it reports an identity.
     */
    refetchInterval: (q) => (shouldKeepAsking(q.state.data?.activated) ? 1500 : false),
  });

  /*
   * Every screen below can BE the whole app, and the till window has no title
   * bar, no border and no taskbar — so a screen with no exit is a trap. Our user
   * hit two of them: a closed shift, and a boot that could not reach the agent.
   * The exit used to live only in the till's menu, which needs an open shift.
   */
  if (target === null) {
    return <Booting />;
  }

  /*
   * Nothing answered on the API, so say that rather than offering a login.
   *
   * This screen replaces the one that caused the outage it is named after: a
   * till whose bundle could not reach its agent used to fall through to the
   * cloud login, where the only button reports «ارسال کد ناموفق بود» -- which
   * blames the SMS gateway for a problem that has nothing to do with it.
   */
  if (target === 'unreachable') {
    return (
      <>
        <CannotReachAgent onRetry={() => { setTarget(null); setProbe((n) => n + 1); }} />
        <ExitCorner />
      </>
    );
  }

  if (target === 'cloud' && !authed) {
    return (
      <>
        <Login onDone={() => { setAuthed(true); void qc.invalidateQueries(); }} />
        <ExitCorner />
      </>
    );
  }

  /*
   * An unpaired till gets the setup wizard and nothing else.
   *
   * Before the session, before the shift: both assume an identity this device
   * does not have. A fresh install used to fall through to the open-shift
   * screen, report itself online and fully synced, and fail with an internal
   * error that named a clock -- which is true and useless. Only treated as
   * unpaired on an explicit false, so a status call that has not landed yet
   * does not flash a setup screen at a working shop.
   */
  if (target === 'agent' && needsSetup(status.data?.activated)) {
    return <SetupScreen onPaired={() => { void qc.invalidateQueries(); }} />;
  }

  if (session.isLoading || !session.data) {
    return <Booting />;
  }

  if (!session.data.shiftId) {
    return (
      <>
        <OpenShiftScreen
          session={session.data}
          onOpened={() => void qc.invalidateQueries({ queryKey: ['till-session'] })}
        />
        <ExitCorner />
      </>
    );
  }

  return (
    <Till
      session={session.data}
      onShiftClosed={() => void qc.invalidateQueries({ queryKey: ['till-session'] })}
    />
  );
}

/*
 * The holding screen at boot — and the one that strands a till hardest.
 *
 * It renders while the agent cannot be reached, which is exactly when a person
 * most needs to get out and look at the machine. It showed for an hour on a real
 * counter with no way off it.
 */
/**
 * The till cannot reach its API.
 *
 * Names the loopback address on purpose: when this screen appears the useful
 * question is whether the agent service is running, and the address is what
 * someone needs in order to check.
 */
function CannotReachAgent({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: 'var(--luma-surface-desk)', padding: 24,
      }}
    >
      <div style={{ maxWidth: 440, textAlign: 'center', display: 'grid', gap: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--luma-ink)' }}>
          صندوق به سرویس محلی وصل نیست
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.9, color: 'var(--luma-muted)' }}>
          این صفحه یعنی سرویس «Luma Store agent» جواب نمی‌دهد — نه اینکه رمز یا
          شماره‌ای اشتباه باشد. اگر سرویس تازه راه‌اندازی شده، چند ثانیه بعد
          دوباره تلاش کنید؛ وگرنه باید سرویس روی همین دستگاه بررسی شود.
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--luma-muted)', fontFamily: 'monospace', direction: 'ltr' }}>
          {apiBase()}
        </div>
        <button
          onClick={onRetry}
          style={{
            justifySelf: 'center', marginTop: 4, padding: '11px 26px', borderRadius: 12,
            fontWeight: 800, fontSize: 15, cursor: 'pointer', border: 'none',
            background: 'var(--luma-accent)', color: '#fff',
          }}
        >
          تلاش دوباره
        </button>
      </div>
    </div>
  );
}

function Booting() {
  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: 'var(--luma-surface-desk)', color: 'var(--luma-muted)', fontSize: 15,
      }}
    >
      <ExitCorner />
      در حال آماده‌سازی صندوق…
    </div>
  );
}
